import { useState, useEffect, useRef } from 'react'
import { ChevronDown, Printer, Map, RefreshCw } from 'lucide-react'
import leiloesService from '../../services/leiloesService'
import mapasService from '../../services/mapasService'
import { useOrganizador } from '../../contexts/OrganizadorContext'

const API_BASE = (import.meta.env.VITE_API_URL || 'http://localhost:8001').replace(/\/$/, '')
const brl = v => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v || 0)
const fmtDate = s => { if (!s) return ''; try { return new Date(s).toLocaleDateString('pt-BR') } catch { return s } }

function Select({ label, value, onChange, children, disabled, className = '' }) {
  return (
    <div className={`flex flex-col gap-1 ${className}`}>
      {label && <label className="text-xs text-gray-500 font-medium">{label}</label>}
      <div className="relative">
        <select value={value} onChange={e => onChange(e.target.value)} disabled={disabled}
          className="w-full appearance-none border border-gray-300 rounded-lg px-3 py-2 pr-8 text-sm bg-white text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:text-gray-400">
          {children}
        </select>
        <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
      </div>
    </div>
  )
}

// Definição das colunas disponíveis
const COLUNAS_DEF = {
  lote:           { label: 'Lote',           align: 'left' },
  qtd:            { label: 'Qtd',            align: 'center' },
  sexo:           { label: 'Sexo',           align: 'center' },
  idade:          { label: 'Idade',          align: 'center' },
  vendedor:       { label: 'Vendedor',       align: 'left' },
  fazenda:        { label: 'Fazenda',        align: 'left' },
  tipo:           { label: 'Tipo',           align: 'left' },
  peso:           { label: 'Peso',           align: 'right' },
  comissao:       { label: 'Comissão',       align: 'right' },
  valor_unitario: { label: 'Valor Unitário', align: 'right' },
  valor_total:    { label: 'Valor Total',    align: 'right' },
}
const ORDEM_COLUNAS = ['lote', 'qtd', 'sexo', 'idade', 'vendedor', 'fazenda', 'tipo', 'peso', 'comissao', 'valor_unitario', 'valor_total']
const DEFAULT_COLUNAS = ['lote', 'qtd', 'vendedor', 'tipo', 'comissao', 'valor_unitario', 'valor_total']

function sortLoteNumerico(a, b) {
  const parse = (s) => {
    const m = String(s || '').trim().match(/^(\d+)(.*)$/)
    if (m) return [parseInt(m[1], 10), m[2].toUpperCase()]
    return [0, String(s || '').toUpperCase()]
  }
  const [numA, sufA] = parse(a.numero_lote)
  const [numB, sufB] = parse(b.numero_lote)
  if (numA !== numB) return numA - numB
  return sufA.localeCompare(sufB)
}

function getCellValue(lote, col, modoPeso) {
  switch (col) {
    case 'lote': return lote.numero_lote
    case 'vendedor': return lote.vendedor
    case 'fazenda': return lote.fazenda || '-'
    case 'tipo': return lote.descricao
    case 'sexo': return lote.sexo || '-'
    case 'idade': return lote.idade || '-'
    case 'qtd': return lote.qtd
    case 'peso': {
      const peso = parseFloat(lote.peso) || 0
      if (!peso) return '-'
      if (modoPeso === 'peso_arroba') {
        const arroba = peso / 30
        return `${peso.toLocaleString('pt-BR', { minimumFractionDigits: 2 })} - ${arroba.toFixed(2)}@`
      }
      return peso.toLocaleString('pt-BR', { minimumFractionDigits: 2 })
    }
    case 'comissao': return brl(lote.comissao)
    case 'valor_unitario': return brl(lote.vv_unit)
    case 'valor_total': return brl(lote.vv)
    default: return ''
  }
}

const S = {
  page: { fontFamily: 'Arial, Helvetica, sans-serif', fontSize: 13, color: '#000', background: '#fff', padding: '20px 28px 24px' },
  hdr: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '2px solid #1f2937', paddingBottom: 14, marginBottom: 18 },
  hdrLogo: { maxHeight: 80, maxWidth: 180, objectFit: 'contain' },
  hdrCenter: { flex: 1, textAlign: 'center', padding: '0 16px' },
  hdrT1: { fontSize: 20, fontWeight: 700 },
  hdrT2: { fontSize: 13, color: '#374151', marginTop: 3 },
  hdrT3: { fontSize: 13, fontWeight: 600, color: '#1f2937', marginTop: 2 },
  tbl: { width: '100%', borderCollapse: 'collapse', fontSize: 12, marginBottom: 10 },
  th: { borderBottom: '2px solid #d1d5db', padding: '8px 8px', textAlign: 'left', fontWeight: 700, fontSize: 11 },
  thC: { borderBottom: '2px solid #d1d5db', padding: '8px 8px', textAlign: 'center', fontWeight: 700, fontSize: 11 },
  thR: { borderBottom: '2px solid #d1d5db', padding: '8px 8px', textAlign: 'right', fontWeight: 700, fontSize: 11 },
  td: { borderBottom: '1px solid #e5e7eb', padding: '7px 8px', verticalAlign: 'middle', fontSize: 12 },
  tdC: { borderBottom: '1px solid #e5e7eb', padding: '7px 8px', textAlign: 'center', verticalAlign: 'middle', fontSize: 12 },
  tdR: { borderBottom: '1px solid #e5e7eb', padding: '7px 8px', textAlign: 'right', verticalAlign: 'middle', fontSize: 12 },
  totHdr: { background: '#f3f4f6', fontWeight: 700, borderTop: '2px solid #1f2937', borderBottom: '1px solid #d1d5db' },
  totRow: { borderBottom: '1px solid #e5e7eb' },
  footer: { textAlign: 'left', fontSize: 11, color: '#6b7280', marginTop: 20, paddingTop: 10, borderTop: '1px solid #e5e7eb' },
}

export default function MapaGeralDefesas() {
  const { organizadorAtivo } = useOrganizador()
  const orgId = organizadorAtivo?.id

  const [leiloes, setLeiloes] = useState([])
  const [leilaoId, setLeilaoId] = useState('')
  const [dados, setDados] = useState(null)
  const [loading, setLoading] = useState(false)
  const [loadingL, setLoadingL] = useState(true)
  const [loadingPdf, setLoadingPdf] = useState(false)
  const [pdfUrl, setPdfUrl] = useState(null)
  const [showPdf, setShowPdf] = useState(false)
  const [mapaConfig, setMapaConfig] = useState(null)
  const iframeRef = useRef(null)

  // Carregar config de mapas
  useEffect(() => {
    if (!orgId) return
    mapasService.getConfig(orgId)
      .then(cfg => setMapaConfig(cfg))
      .catch(() => {})
  }, [orgId])

  useEffect(() => {
    if (!orgId) return
    setLoadingL(true)
    leiloesService.getByOrganizador(orgId)
      .then(data => {
        const sorted = [...(data || [])].sort((a, b) => new Date(b.data_leilao) - new Date(a.data_leilao))
        setLeiloes(sorted)
        if (sorted.length) setLeilaoId(String((sorted.find(l => l.status === 'on') || sorted[0]).id))
      })
      .finally(() => setLoadingL(false))
  }, [orgId])

  useEffect(() => {
    if (!orgId || !leilaoId) { setDados(null); return }
    setLoading(true)
    mapasService.getMapaGeralDados(orgId, leilaoId, 'liquido')
      .then(d => {
        const defesas = (d.lotes || []).filter(l => l.situacao === 'DEFESA').map(l => ({
          ...l,
          comissao: l.vv * parseFloat(l.cv_pct) / 100,
        }))
        // Ordenar por número do lote (numérico)
        defesas.sort(sortLoteNumerico)
        setDados({ ...d, lotes: defesas })
      })
      .catch(() => setDados(null))
      .finally(() => setLoading(false))
  }, [orgId, leilaoId])

  // Determinar colunas ativas
  const colunasAtivas = (() => {
    if (mapaConfig?.mapa_defesas_colunas_ativas && Array.isArray(mapaConfig.mapa_defesas_colunas_ativas) && mapaConfig.mapa_defesas_colunas_ativas.length > 0) {
      return ORDEM_COLUNAS.filter(c => mapaConfig.mapa_defesas_colunas_ativas.includes(c))
    }
    return DEFAULT_COLUNAS
  })()
  const modoPeso = mapaConfig?.mapa_defesas_modo_peso || 'peso_arroba'

  const getHeaderLabel = (col) => {
    if (col === 'peso') return modoPeso === 'peso_arroba' ? 'Peso (Kg/@)' : 'Peso (Kg)'
    return COLUNAS_DEF[col]?.label || col
  }

  const getThStyle = (col) => {
    const align = COLUNAS_DEF[col]?.align || 'left'
    if (align === 'center') return S.thC
    if (align === 'right') return S.thR
    return S.th
  }

  const getTdStyle = (col) => {
    const align = COLUNAS_DEF[col]?.align || 'left'
    if (align === 'center') return S.tdC
    if (align === 'right') return S.tdR
    return S.td
  }

  const leilaoAtual = leiloes.find(l => String(l.id) === leilaoId)
  const logoOrgSrc = organizadorAtivo?.logo_url
    ? (organizadorAtivo.logo_url.startsWith('http') ? organizadorAtivo.logo_url
        : `${API_BASE}${organizadorAtivo.logo_url.startsWith('/') ? '' : '/'}${organizadorAtivo.logo_url}`)
    : null

  const handleImprimir = async () => {
    setLoadingPdf(true)
    try {
      const blob = await mapasService.getMapaGeralDefesasPdf(orgId, leilaoId)
      if (pdfUrl) URL.revokeObjectURL(pdfUrl)
      const url = URL.createObjectURL(new Blob([blob], { type: 'application/pdf' }))
      setPdfUrl(url)
      setShowPdf(true)
    } catch (err) { alert('Erro: ' + (err.response?.statusText || err.message)) }
    finally { setLoadingPdf(false) }
  }
  const closePdf = () => { setShowPdf(false); if (pdfUrl) { URL.revokeObjectURL(pdfUrl); setPdfUrl(null) } }
  const printPdf = () => { if (iframeRef.current?.contentWindow) { iframeRef.current.contentWindow.focus(); iframeRef.current.contentWindow.print() } }

  const totalLotes = dados?.lotes?.length || 0
  const totalQtd = dados?.lotes?.reduce((s, l) => s + l.qtd, 0) || 0
  const totalValor = dados?.lotes?.reduce((s, l) => s + l.vv, 0) || 0

  if (!orgId) return <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-700">Selecione um organizador.</div>

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-1.5 flex-shrink-0">
        <Map className="w-5 h-5 text-orange-500" />
        <h1 className="text-lg font-semibold text-gray-800">Mapa Geral de Defesas</h1>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
        <div className="flex flex-wrap items-end gap-3">
          <Select label="Leilão" value={leilaoId} onChange={v => { setLeilaoId(v); setDados(null) }} disabled={loadingL} className="flex-[2] min-w-[200px]">
            {loadingL ? <option>Carregando...</option> : leiloes.map(l => <option key={l.id} value={String(l.id)}>{l.nome}</option>)}
          </Select>
          <div className="flex items-end gap-2 ml-auto">
            {loading && <RefreshCw size={15} className="text-gray-400 animate-spin mb-2" />}
            <button onClick={handleImprimir} disabled={!dados || !dados.lotes?.length || loadingPdf}
              className="flex items-center gap-2 px-4 py-2 h-9 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-200 disabled:cursor-not-allowed text-white rounded-lg text-sm font-medium transition-colors">
              <Printer size={15} /> Imprimir
            </button>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="bg-white rounded-xl border border-gray-200 p-12 flex justify-center"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" /></div>
      ) : !dados || !dados.lotes?.length ? (
        <div className="bg-white rounded-xl border border-gray-200 p-12 text-center text-gray-400 text-sm">Nenhuma defesa encontrada</div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <div style={S.page}>
            <div style={S.hdr}>
              <div style={{ minWidth: 140 }}>{logoOrgSrc ? <img src={logoOrgSrc} alt="logo" style={S.hdrLogo} /> : <span style={{ fontWeight: 700 }}>{organizadorAtivo?.name || ''}</span>}</div>
              <div style={S.hdrCenter}>
                <div style={S.hdrT1}>MAPA GERAL DE DEFESAS</div>
                <div style={S.hdrT2}>{organizadorAtivo?.name || organizadorAtivo?.nome || ''}</div>
                <div style={S.hdrT3}>{leilaoAtual?.nome} — {fmtDate(leilaoAtual?.data_leilao)}</div>
              </div>
              <div style={{ minWidth: 140, textAlign: 'right' }}><img src="/assets/rematei.png" alt="Rematei" style={S.hdrLogo} /></div>
            </div>

            <table style={S.tbl}>
              <thead><tr>
                {colunasAtivas.map(col => (
                  <th key={col} style={getThStyle(col)}>{getHeaderLabel(col)}</th>
                ))}
              </tr></thead>
              <tbody>
                {dados.lotes.map((l, i) => (
                  <tr key={i}>
                    {colunasAtivas.map(col => (
                      <td key={col} style={getTdStyle(col)}>
                        {col === 'lote' ? <strong>{getCellValue(l, col, modoPeso)}</strong> :
                         col === 'vendedor' ? <div style={{ fontWeight: 600 }}>{getCellValue(l, col, modoPeso)}</div> :
                         getCellValue(l, col, modoPeso)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>

            <table style={{ ...S.tbl, marginTop: 12 }}>
              <thead><tr style={S.totHdr}>
                <td style={{ ...S.td, fontWeight: 700, width: '50%' }}>TOTAL</td>
                <td style={{ ...S.tdC, fontWeight: 700, width: '15%' }}>LOTES</td>
                <td style={{ ...S.tdC, fontWeight: 700, width: '15%' }}>QTD</td>
                <td style={{ ...S.tdR, fontWeight: 700, width: '20%' }}>VALOR</td>
              </tr></thead>
              <tbody>
                <tr style={S.totRow}><td style={S.td}>Defesas</td><td style={S.tdC}>{totalLotes}</td><td style={S.tdC}>{totalQtd}</td><td style={S.tdR}>{brl(totalValor)}</td></tr>
                {colunasAtivas.includes('comissao') && (
                  <tr style={S.totRow}><td style={S.td}>Comissão defesa</td><td style={S.tdC}></td><td style={S.tdC}></td><td style={S.tdR}>{brl(dados.resumo?.comissao_defesa || 0)}</td></tr>
                )}
              </tbody>
            </table>

            <div style={S.footer}>www.rematei.com.br</div>
          </div>
        </div>
      )}

      {showPdf && pdfUrl && (
        <div className="fixed inset-0 bg-black/60 flex items-end sm:items-center justify-center z-50 sm:p-4">
          <div className="bg-white w-full max-w-5xl flex flex-col rounded-t-2xl sm:rounded-xl shadow-2xl" style={{ height: '92dvh' }}>
            <div className="flex items-center gap-2 px-4 py-3 border-b border-gray-200 bg-gray-50 rounded-t-2xl sm:rounded-t-xl flex-shrink-0">
              <span className="font-semibold text-gray-800 text-sm truncate flex-1 min-w-0">Mapa Geral de Defesas</span>
              <div className="flex items-center gap-1.5 flex-shrink-0">
                <button onClick={printPdf} className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors"><Printer size={15} /> Imprimir</button>
                <button onClick={closePdf} className="px-3 py-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors text-sm">Fechar</button>
              </div>
            </div>
            <div className="flex-1 overflow-hidden rounded-b-xl"><iframe ref={iframeRef} src={pdfUrl} title="Mapa Geral Defesas" style={{ width: '100%', height: '100%', border: 'none' }} /></div>
          </div>
        </div>
      )}
    </div>
  )
}
